import { ActionOrigin } from 'base/analytics';
import { Button } from 'base/ui/button';
import { IconAdd } from 'base/ui/icons';
import { menuItem } from 'base/ui/menu_model';
import { ElementNodeEditingActions } from 'blocks/editor/editing_actions/element_node';
import {
  ADD_ABOVE_ACTION_POSITION,
  ADD_BELOW_ACTION_POSITION,
  ADD_LEFT_ACTION_POSITION,
  ADD_RIGHT_ACTION_POSITION,
  SelectionFrameAction,
} from 'blocks/editor/selection/frame';
import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import React from 'react';
import styles from './slot_adding.css';

export function getSlotItemAddButton(
  editing: ElementNodeEditingActions,
  direction: 'before' | 'after',
  color: 'global' | undefined,
): SelectionFrameAction | undefined {
  const { parentSlot } = editing;
  if (!parentSlot) {
    return;
  }
  const pos =
    parentSlot.type.dragHandlePosition === 'top'
      ? direction === 'after'
        ? ADD_RIGHT_ACTION_POSITION
        : ADD_LEFT_ACTION_POSITION
      : direction === 'after'
      ? ADD_BELOW_ACTION_POSITION
      : ADD_ABOVE_ACTION_POSITION;

  return {
    pos,
    content: (
      <SlotItemAddButton
        editing={editing}
        direction={direction}
        color={color}
      />
    ),
    showWhenSelected: true,
    showWhenHovering: true,
    maxWidthRatio: 1,
    maxHeightRatio: 2,
  };
}

const SlotItemAddButton = observer(function _SlotItemAddButton({
  editing,
  direction,
  color,
}: {
  editing: ElementNodeEditingActions;
  direction: 'before' | 'after';
  color: 'global' | undefined;
}) {
  const { addItemAround } = editing;
  return typeof addItemAround === 'function' ? (
    <ExpandingAddButton
      onClick={() => addItemAround(direction, ActionOrigin.Button)}
      color={color}
    />
  ) : null;
});

function ExpandingAddButton({
  onClick,
  color,
}: {
  onClick: () => void;
  color: 'global' | undefined;
}) {
  return (
    <div className={styles.expandingAddButton}>
      <div
        className={classNames(styles.dot, color === 'global' && styles.global)}
      />
      <div className={styles.realButton}>
        <Button
          variant="primary"
          background={color ?? 'ai'}
          icon={IconAdd}
          size="small"
          onClick={onClick}
        />
      </div>
    </div>
  );
}

export function getSlotItemAddingMenuItems(editing: ElementNodeEditingActions) {
  const { addItemAround, parentSlot, flags } = editing;
  if (!parentSlot) return [];
  const position = parentSlot.type.dragHandlePosition;
  const beforeName = position === 'top' ? 'left' : 'above';
  const afterName = position === 'top' ? 'right' : 'below';
  return [
    menuItem({
      label: `Add element ${beforeName}`,
      disabled: typeof addItemAround !== 'function',
      tooltip: typeof addItemAround === 'string' ? addItemAround : undefined,
      onClick:
        typeof addItemAround === 'function'
          ? () => addItemAround('before', ActionOrigin.ContextMenu)
          : undefined,
    }),
    menuItem({
      label: `Add element ${afterName}`,
      disabled: typeof addItemAround !== 'function',
      tooltip: typeof addItemAround === 'string' ? addItemAround : undefined,
      onClick:
        typeof addItemAround === 'function'
          ? () => addItemAround('after', ActionOrigin.ContextMenu)
          : undefined,
    }),
  ];
}
